import {
  ServiceProviderUnitAssignmentCount,
  SocialProviderUnitDto,
} from '../../../../api-service/clients';
import { formatSocialProviderUnitByName } from '../../../offer-and-activity/utilities';

export type AssignmentCountsByUnitId = ReadonlyMap<
  string,
  ServiceProviderUnitAssignmentCount
>;

export function toAssignmentCountsByUnitId(
  counts: ServiceProviderUnitAssignmentCount[]
): AssignmentCountsByUnitId {
  return new Map(counts.map(count => [count.serviceProviderUnitId, count]));
}

function describeCount(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function assignmentNoun(lastCount: number) {
  return lastCount === 1 ? 'bestilling' : 'bestillinger';
}

export function describeAssignmentCounts(
  currentCount: number,
  historicCount: number
) {
  const parts = [
    currentCount > 0
      ? describeCount(currentCount, 'aktuel', 'aktuelle')
      : undefined,
    historicCount > 0
      ? describeCount(historicCount, 'historisk', 'historiske')
      : undefined,
  ].filter((part): part is string => !!part);

  if (parts.length === 0) {
    return undefined;
  }

  const lastCount = historicCount > 0 ? historicCount : currentCount;
  return `(${parts.join(', ')} ${assignmentNoun(lastCount)})`;
}

function countsFor(
  unit: SocialProviderUnitDto,
  countsByUnitId: AssignmentCountsByUnitId
) {
  const count = unit.id ? countsByUnitId.get(unit.id) : undefined;
  return {
    current: count?.currentAssignmentCount ?? 0,
    historic: count?.historicAssignmentCount ?? 0,
  };
}

export function formatUnitWithAssignmentCounts(
  unit: SocialProviderUnitDto,
  countsByUnitId: AssignmentCountsByUnitId
) {
  const { current, historic } = countsFor(unit, countsByUnitId);
  const counts = describeAssignmentCounts(current, historic);
  const unitLabel = formatSocialProviderUnitByName(unit);
  return counts ? `${unitLabel} ${counts}` : unitLabel;
}

export function compareUnitsByAssignmentCounts(
  a: SocialProviderUnitDto,
  b: SocialProviderUnitDto,
  countsByUnitId: AssignmentCountsByUnitId
) {
  const countsA = countsFor(a, countsByUnitId);
  const countsB = countsFor(b, countsByUnitId);
  return (
    countsB.current - countsA.current ||
    countsB.historic - countsA.historic ||
    formatSocialProviderUnitByName(a).localeCompare(
      formatSocialProviderUnitByName(b)
    )
  );
}
